import {useInfiniteQuery, UseInfiniteQueryOptions, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {useAddressContext} from 'components/providers';
import {useRouter} from 'next/router';
import {useEffect, useRef} from 'react';
import {
  IProductsResponse,
  ISearchAutocompleteItem,
  ISearchProductsResponse,
  ProviderTypeEnum,
  QueryKeyEnum,
} from 'types';

import {api} from '../../setup';

export const useSearchRestaurants = (
  selectedIndex: number,
  query: string,
  options?: UseQueryOptions<IProductsResponse>,
) => {
  const queryProviderPrefix = [0, 1].includes(selectedIndex) ? 'restaurants' : 'markets';
  const {selectedAddress, isAddressesLoading} = useAddressContext();

  const enabledRef = useRef<boolean>(false);
  const queryRe = useQuery<any>({
    queryKey: [QueryKeyEnum.SEARCH_PROVIDERS, query, selectedIndex],
    queryFn: () => api.get(`/${queryProviderPrefix}/provider-search`, {params: query}),
    enabled: !!query && enabledRef.current,
    ...options,
  });

  const refetchQuery = queryRe.refetch;
  useEffect(() => {
    if (isAddressesLoading) return;
    enabledRef.current = true;
    refetchQuery();
  }, [selectedAddress?.id, isAddressesLoading, refetchQuery]);
  return queryRe;
};

export const useSearchAutocomplete = (
  providerType: ProviderTypeEnum = ProviderTypeEnum.RESTAURANT,
  options?: UseQueryOptions,
) => {
  const {selectedAddress} = useAddressContext();
  return useQuery<unknown, unknown, ISearchAutocompleteItem[], any>({
    queryKey: [QueryKeyEnum.SEARCH_AUTOCOMPLETE, providerType, selectedAddress?.id, selectedAddress?.address?.id],
    queryFn: () =>
      api.get(`/search-autocomplete/data`, {
        params: {type: providerType},
      }),
    enabled: false,
    staleTime: 30000,
    ...options,
  });
};

export const useInfiniteSearchProducts = (
  selectedIndex: number,
  params: {query: string; type?: string},
  options?: UseInfiniteQueryOptions<ISearchProductsResponse>,
) => {
  const queryProviderPrefix = [0, 1].includes(selectedIndex) ? 'restaurants' : 'markets';
  const {locale} = useRouter();

  const enabled = params?.query ? true : false;
  return useInfiniteQuery({
    queryKey: [QueryKeyEnum.SEARCH_PRODUCTS, params, locale],
    queryFn: ({pageParam = 0}) => {
      return api.get(`/${queryProviderPrefix}/product-search`, {
        params: {
          ...params,
          paginate_offset: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.result.length) return null;
      const nextOffset = lastPage?.paginate_offset;
      return nextOffset ? nextOffset : null;
    },
    enabled,
    ...options,
  });
};

export const useSearchRestaurantsByCategory = (
  categorySlug: string,
  options?: UseInfiniteQueryOptions<ISearchProductsResponse>,
) => {
  const {locale} = useRouter();
  return useInfiniteQuery({
    queryKey: [QueryKeyEnum.SEARCH_RESTAURANTS_BY_CATEGORY, categorySlug, locale],
    queryFn: ({pageParam = 0}) => {
      return api.get(`/restaurants/by-category`, {
        params: {
          category_slug: categorySlug,
          paginate_offset: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.result.length) return null;
      const nextOffset = lastPage?.paginate_offset;
      return nextOffset ? nextOffset : null;
    },
    enabled: !!categorySlug,
    ...options,
  });
};
